import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, takeUntil, Subject } from 'rxjs';
import { Task, TaskCounts, SubletVendorResponse, SubletVendorAssignment, TaskProgressResponse, BlueprintProgressResponse } from '@org/interfaces';
import { environment } from '../../environments/environment';
import { UserService } from '@org/shared-services';
import { SysNotifyService } from '@org/shared-services';

@Injectable({
  providedIn: 'root'
})
export class TaskService implements OnDestroy {
  private apiUrl = `${environment.apiUrl}/api/tasks`;
  private tasksSubject = new BehaviorSubject<Task[]>([]);
  public tasks$ = this.tasksSubject.asObservable();
  private totalTasksSubject = new BehaviorSubject<number>(0);
  public totalTasks$ = this.totalTasksSubject.asObservable();
  private totalTasksReadSubject = new BehaviorSubject<TaskCounts>({
    incoming: 0,
    incoming_card: 0,
    outgoing: 0,
    outgoing_card: 0,
    allTechRequests: 0,
    allTechRequests_card: 0,
    myTechRequests: 0,
    techQueue: 0,
    myTechRequests_card: 0,
    overDueTechRequestsByRole: 0,
    overDueTechRequests: 0,
    csrPending: 0,
    csrPending_card: 0,
    porterPending: 0,
    porterPending_card: 0
  });
  public totalTasksRead$ = this.totalTasksReadSubject.asObservable();

  private subletVendorsSubject = new BehaviorSubject<SubletVendorAssignment[]>([]);
  public subletVendors$ = this.subletVendorsSubject.asObservable();
  
  
  private taskUpdateSubject = new BehaviorSubject<boolean>(false);
  taskUpdates$ = this.taskUpdateSubject.asObservable();

  // Add new BehaviorSubject for processed tasks
  private processedTasksSubject = new BehaviorSubject<Task[]>([]);
  public processedTasks$ = this.processedTasksSubject.asObservable();

  private destroy$ = new Subject<void>();

  constructor(private http: HttpClient, private userService: UserService, private sysNotifyService: SysNotifyService) { }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Method to trigger task updates, notifying all subscribers
  triggerTaskUpdate() {
    this.taskUpdateSubject.next(true);
  }

  createTask(task: any): Observable<Task> {
    return this.http.post<Task>(`${this.apiUrl}/tasks`, task);
  }

  createRequest(task: any): Observable<any> {
    return this.http.post<Task>(`${this.apiUrl}/tasksr`, task);
  }

  createRequestWithPhotos(formData: FormData): Observable<any> {
    return this.http.post<Task>(`${this.apiUrl}/tasksr/with-photos`, formData);
  }

  // Get a single task by ID
  getTask(id: string): Observable<Task> {
    return this.http.get<Task>(`${this.apiUrl}/tasks/${id}`);
  }

  updateTask(id: string, task: Task, comments?: string, action = ''): Observable<any> { // update | complete | reassign
    return this.http.patch<any>(`${this.apiUrl}/tasks/${id}/status`, {
      action: action,
      comments: comments,
      task: task
    }).pipe(
      tap((updatedTask) => {
        // Get current tasks from subject
        const currentTasks = this.tasksSubject.getValue();
        // Update the task in the array
        const updatedTasks = currentTasks.map(t => 
          t._id === updatedTask.task._id ? updatedTask.task : t
        );
        // Emit the updated tasks array
        this.tasksSubject.next(updatedTasks);
        // Trigger task update notification
        this.triggerTaskUpdate();
      })
    );
  }

  updateTaskRead(id: string ): void { // update | complete | reassign
    this.http.patch<Task>(`${this.apiUrl}/read/${id}`, {
      isRead: true
    }).subscribe({
      next: (data) => {
        this.getTaskCounts();
      },
      error: (error) => {
        console.error('Error updating task read status', error);
    }
    });
  }

  
  reassignTaskByRole(id: string, newRole: string, task: Task, comments?: string): Observable<Task> {
    return this.http.patch<Task>(`${this.apiUrl}/tasks/${id}/reassign`, {
      newRole: newRole,
      comments: comments,
      task: task
    });
  }

  assignTaskToUser(task: Task, action: boolean): Observable<Task> {
    return this.http.patch<Task>(`${this.apiUrl}/assignToUser`, {
      task: task,
      action: action
    });
  }

  assignTaskToEmpl(taskId: string, newAssigneeId: string ): Observable<Task> {
    return this.http.patch<Task>(`${this.apiUrl}/assignToEmpl`, {
      taskId: taskId,
      newAssigneeId: newAssigneeId
    });
  }

  assignTaskToRole(taskId: string, roleId: string, userId: string): Observable<any> {
    return this.http.patch<any>(`${this.apiUrl}/assign`, { taskId, roleId, userId });
  }

  // Delete a task by ID
  deleteTask(id: string | undefined): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/tasks/${id}`);
  }

  // Get all tasks for the current tenant
  getTasks(page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/tenant?page=${page}&limit=${limit}`).pipe(
      tap(data => {
        this.tasksSubject.next(data.tasks);
        this.totalTasksSubject.next(data.totalPages);
      })
    );
  }

  // Get all tasks for a given user within a tenant
  getAllTasksByUser(userId: string, page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/user?userId=${userId}&page=${page}&limit=${limit}`).pipe(
      tap(data => {
        this.tasksSubject.next(data.tasks);
        this.totalTasksSubject.next(data.totalPages);
      })
    );
  }

   // Get all tasks for a given user within a tenant
   getAllTechTasksByUser(userId: string, page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/tuser?userId=${userId}&page=${page}&limit=${limit}`).pipe(
      tap(data => {
        this.tasksSubject.next(data.tasks);
        this.totalTasksSubject.next(data.totalPages);
      })
    );
  }

  // Get all tasks for a given role within a tenant
  getAllTasksByRole(page = 1, limit = 10, roleName: string): void {
    this.http.get<any>(`${this.apiUrl}/role?page=${page}&limit=${limit}&roleName=${roleName}`).subscribe(data => {
      this.tasksSubject.next(data.tasks);
      this.totalTasksSubject.next(data.totalPages);
    });
  }

  // Get all tasks for a given role within a tenant
  getTasksByRole(page = 1, limit = 10): void {
    this.http.get<any>(`${this.apiUrl}/by-role?page=${page}&limit=${limit}`).subscribe(data => {
      this.tasksSubject.next(data.tasks);
      this.totalTasksSubject.next(data.totalPages);
    });
  }

  getAllTasks(tenantId: any): Observable<any> {
    return this.http.get<Task[]>(`${this.apiUrl}/tenant?tenant=${tenantId}`);
  }

  // Get all tasks for a given user within a tenant
  getAllTasksCreatedByUser(userId: string, page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/tasksbyuser?userId=${userId}&page=${page}&limit=${limit}`).pipe(
      tap(data => {
        this.tasksSubject.next(data.tasks);
        this.totalTasksSubject.next(data.totalPages);
      })
    );
  }

  getAllTechRequests(userId: string, page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/tuser?userId=${userId}&page=${page}&limit=${limit}`).pipe(
      tap(data => {
        this.tasksSubject.next(data.tasks);
        this.totalTasksSubject.next(data.totalPages);
      })
    );
  }

  // Method to get task counts
  getTaskCounts(): void {
    this.http.get<TaskCounts>(`${this.apiUrl}/counts`).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (counts: TaskCounts) => {
        this.totalTasksReadSubject.next(counts);
      },
      error: (error: Error) => {
        console.error('Error loading task counts:', error);
        this.sysNotifyService.showError('Error loading task counts', 3000, 'right', 'top');
      }
    });
  }

  // // Get Task completion report data
  // getCompletionReport(): Observable<any> {
  //   return this.http.get<any>(`${this.apiUrl}/completion-report`);
  // }

   // Get Task completion report data
  getTaskCompletionReport(startDate: string, endDate: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/completion-report?startDate=${startDate}&endDate=${endDate}`);
  }

  // Add new method to update processed tasks
  updateProcessedTasks(tasks: Task[]) {
    this.processedTasksSubject.next(tasks);
    // Also update the main tasks subject to maintain consistency
    this.tasksSubject.next(tasks);
  }

  // Add this public method to TaskService
  updateTasks(tasks: Task[]) {
    this.tasksSubject.next(tasks);
  }

  getTasksWithSublets(): Observable<Task[]> {
    return this.http.get<Task[]>(`${this.apiUrl}/tasks/sublets`);
  }

  getSubletVendorAssignments(taskId: string): Observable<SubletVendorResponse> {
    return this.http.get<SubletVendorResponse>(`${this.apiUrl}/tasks/${taskId}/sublet-vendors`);
  }

  // Get CSR pending tasks
  getCSRPendingTasks(page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/csr/pending?page=${page}&limit=${limit}`).pipe(
      tap(data => {
        this.tasksSubject.next(data.tasks);
        this.totalTasksSubject.next(data.totalPages);
      })
    );
  }

  // Get CSR closed tasks
  getCSRClosedTasks(page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/csr/closed?page=${page}&limit=${limit}`).pipe(
      tap(data => {
        this.tasksSubject.next(data.tasks);
        this.totalTasksSubject.next(data.totalPages);
      })
    );
  }

  // Close a task as CSR
  csrCloseTask(taskId: string, data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/csr/close/${taskId}`, data);
  }

  // Get Porter pending tasks
  getPorterPendingTasks(page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/porter/pending?page=${page}&limit=${limit}`).pipe(
      tap(data => {
        this.tasksSubject.next(data.tasks);
        this.totalTasksSubject.next(data.totalPages);
      })
    );
  }

  // Get Porter closed tasks
  getPorterClosedTasks(page = 1, limit = 10): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/porter/closed?page=${page}&limit=${limit}`).pipe(
      tap(data => {
        this.tasksSubject.next(data.tasks);
        this.totalTasksSubject.next(data.totalPages);
      })
    );
  }

  // Close a task as Porter
  porterCloseTask(taskId: string, data: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/porter/close/${taskId}`, data);
  }

  // Upload task media without sublets
  uploadTaskMedia(taskId: string, files: File[]): Observable<any> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('file', file); // 'file' is the field name expected by the backend
    });
    return this.http.post(`${this.apiUrl}/${taskId}/media`, formData);
  }

  // Delete uploaded task media item
  deleteTaskMedia(taskId: string, mediaId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${taskId}/media/${mediaId}`);
  }

  /**
   * Add a comment to a task
   * @param taskId The ID of the task
   * @param commentData The comment data to post (object with comment text and any other required fields)
   */
  addTaskComment(taskId: string, commentData: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/tasks/${taskId}/comments`, commentData);
  }

  /**
   * Get the blueprint progress for a task
   * @param taskId The ID of the task
   */
  getBlueprintProgress(taskId: string): Observable<BlueprintProgressResponse> {
    return this.http.get<BlueprintProgressResponse>(`${this.apiUrl}/blueprint/${taskId}/progress/detailed`);
  }

}
